import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  Header,
  UpdatedHeader,
  Footer,
  footerItems,
  navigationConfig,
  NavSection,
  getSellerUserMenuItems,
} from "@b2b/ui-components";
import type { HeaderChatUnread } from "./HeaderChatMessages";
import type { HeaderNotifications } from "./HeaderNotifications";

interface AppLayoutProps {
  children: React.ReactNode;
  role: "admin" | "seller" | "buyer" | "both";
  userRole?: "admin" | "seller" | "buyer";
  onLogout?: () => void; // Optional logout handler from AuthProvider
  /** Live chat unread for header badge (from webapp `useChatUnreadHeader`). */
  chatUnread?: HeaderChatUnread;
  /** In-app notifications for header dropdown (from webapp `useNotificationHeader`). */
  notifications?: HeaderNotifications;
  /** In-app notification unread count from backend notifications API. */
  notificationUnreadCount?: number;
  /** When role is seller, override sidebar items (e.g. by approval/profile completion). */
  sellerNavigationItems?: NavSection[];
  /** When role is admin, override sidebar items (e.g. staff module limits). */
  adminNavigationItems?: NavSection[];
  /** Seller only: 0-100 profile completion percentage shown in sidebar. */
  profileCompletionPercentage?: number;
  /** Seller only: use with profileCompleteEnough to derive profile menu (same as sidebar logic). */
  sellerApproved?: boolean;
  /** Seller only: use with sellerApproved to derive profile menu (same as sidebar logic). */
  profileCompleteEnough?: boolean;
  /** Seller only: when not approved, show Storefront in menu only when Step 2 (membership) done. */
  hasMembershipPlanSelected?: boolean;
  /** Seller only: step 3 complete; used for onboarding progress in sidebar. */
  storefrontComplete?: boolean;
  /** Hide shell header (while keeping sidebar/content). */
  hideShellHeader?: boolean;
  /** Hide shell footer (while keeping sidebar/content). */
  hideShellFooter?: boolean;
  /** Optional custom header node (used by webapp-specific header). */
  customShellHeader?: React.ReactNode;
  /** Optional custom footer node (used by webapp-specific footer). */
  customShellFooter?: React.ReactNode;
}

const AppLayout = ({
  children,
  role,
  userRole,
  onLogout,
  chatUnread,
  notifications,
  notificationUnreadCount,
  sellerNavigationItems,
  adminNavigationItems,
  profileCompletionPercentage,
  sellerApproved = false,
  profileCompleteEnough = false,
  hasMembershipPlanSelected = false,
  storefrontComplete = false,
  hideShellHeader = false,
  hideShellFooter = false,
  customShellHeader,
  customShellFooter,
}: AppLayoutProps) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mainScrollRef = useRef<HTMLElement>(null);

  // Scroll the app content region to top on route change. The scroll container is <main>,
  // not the window (see overflow-y-auto), so window.scrollTo used elsewhere does not apply.
  useEffect(() => {
    const el = mainScrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [location.pathname]);

  const sellerUserMenuItems =
    role === "seller"
      ? getSellerUserMenuItems(sellerApproved, profileCompleteEnough, hasMembershipPlanSelected)
      : undefined;

  const sellerOnboardingProgress =
    role === "seller" && !sellerApproved
      ? {
          step1Complete: profileCompleteEnough,
          step2Complete: hasMembershipPlanSelected,
          step3Complete: storefrontComplete,
          waitingApproval: profileCompleteEnough && hasMembershipPlanSelected && storefrontComplete,
        }
      : undefined;

  const isProfilePage = /^\/(admin|seller|buyer)\/(profile|profile_settings|userprofile)(\/|$)/.test(
    location.pathname,
  );
  const isDashboardShellPath =
    location.pathname.startsWith("/buyer") ||
    location.pathname.startsWith("/seller") ||
    location.pathname.startsWith("/admin");
  const useUpdatedShellHeader =
    role === "seller" ||
    isProfilePage ||
    /^\/seller\/(dashboard|analytics|enquiries)(\/|$)/.test(location.pathname) ||
    /^\/chat(\/|$)/.test(location.pathname);

  // Check screen size
  useEffect(() => {
    const checkScreen = () => {
      if (window.innerWidth < 1024) {
        setIsMobile(true);
        setSidebarCollapsed((prev) => (isMobile ? prev : true));
      } else {
        setIsMobile(false);
        setSidebarCollapsed(false); // always open on desktop
      }
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, [isMobile]);

  const toggleSidebar = () => setSidebarCollapsed((prev) => !prev);

  return (
    <div className="flex h-screen overflow-hidden relative">
      {/* Overlay for mobile */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        role={role}
        className={`h-full z-20 transition-transform duration-300
          ${isMobile ? "fixed top-0 left-0" : "sticky top-0"}
          ${isMobile && sidebarCollapsed ? "-translate-x-full" : "translate-x-0"}
        `}
        navigationItems={
          role === "seller" && sellerNavigationItems != null
            ? sellerNavigationItems
            : role === "admin" && adminNavigationItems != null
              ? adminNavigationItems
              : navigationConfig[role]
        }
        profileCompletionPercentage={
          role === "seller" ? profileCompletionPercentage : undefined
        }
        sellerOnboardingProgress={sellerOnboardingProgress}
      />

      {/* Main Layout */}
      <div className="flex flex-1 flex-col h-full min-w-0">
        {/* Header */}
        {!hideShellHeader ? (
          <div className="sticky top-0 z-30 shrink-0">
            {customShellHeader ? (
              React.isValidElement(customShellHeader) ? (
                // Inject sidebar toggle for mobile (custom marketing header replaces UpdatedHeader).
                React.cloneElement(
                  customShellHeader as React.ReactElement<{
                    onToggleSidebar?: () => void;
                    isSidebarOpen?: boolean;
                  }>,
                  {
                    onToggleSidebar: toggleSidebar,
                    isSidebarOpen: !sidebarCollapsed,
                  },
                )
              ) : (
                customShellHeader
              )
            ) : useUpdatedShellHeader ? (
              <UpdatedHeader
                role={role}
                userRole={userRole}
                onToggleSidebar={toggleSidebar}
                isSidebarOpen={!sidebarCollapsed}
                onLogout={onLogout}
                sellerUserMenuItems={sellerUserMenuItems}
                chatUnread={chatUnread}
                notifications={notifications}
                notificationUnreadCount={notificationUnreadCount}
              />
            ) : (
              <Header
                role={role}
                userRole={userRole}
                onToggleSidebar={toggleSidebar}
                isSidebarOpen={!sidebarCollapsed} // 👈 pass state here
                onLogout={onLogout} // Pass logout handler to Header
                sellerUserMenuItems={sellerUserMenuItems}
                chatUnread={chatUnread}
                notifications={notifications}
                notificationUnreadCount={notificationUnreadCount}
              />
            )}
          </div>
        ) : null}

        {/* Scrollable Content */}
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto min-w-0">
          {/* Profile settings pages get no horizontal padding so the hero banner can span full-width */}
          {isProfilePage ? (
            <div className="w-full min-w-0">{children}</div>
          ) : (
            <div className="w-full min-w-0 px-3 sm:px-4 md:px-6 lg:px-8">
              {children}
            </div>
          )}
          {/* Sticky Footer - Hidden on buyer/seller/admin dashboard shells */}
          {!hideShellFooter &&
            !isDashboardShellPath &&
            !location.pathname.includes("/seller/membership_plans") && (
            <div className="shrink-0">
              {customShellFooter ? (
                customShellFooter
              ) : (
                <Footer
                  footerItem={footerItems}
                  bgColor="bg-[var(--color-grey-900)]"
                  textColor="text-gray-300"
                  linkHoverColor="hover:text-orange-400"
                  className="w-full"
                />
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
